<template>
  <div v-loading="loading" class="mp-layout">
    <MarketPortalSidebar />
    <div class="mp-main">
      <div class="mp-panel">
        <MarketFilterBar @scene-change="onSceneChange" />
      </div>

      <div class="mp-panel">
        <div class="mp-sec-head">
          <div class="mp-sec-title">热门模板</div>
          <div class="mp-shuffle" @click="shuffleHot">
            <MarketUviewIcon name="reload" :size="14" color="#2b6de8" />
            <span>换一换</span>
          </div>
        </div>
        <MarketTemplateGrid :ids="hotIds" hot @preview="openPreview" @use="openUse" />
      </div>

      <div class="mp-panel">
        <div class="mp-sec-head">
          <div class="mp-sec-title">节日推荐</div>
          <div class="mp-tabs-row">
            <span
              v-for="(tab, i) in festivalTabs"
              :key="tab.key ?? tab.name"
              class="mp-ftab"
              :class="{ 'is-active': festIndex === i }"
              @click="festIndex = i"
            >
              {{ tab.name }}
            </span>
          </div>
          <div class="mp-sec-extra" @click="goto('cat', -1)">查看更多 ›</div>
        </div>
        <MarketTemplateGrid :ids="festIds" @preview="openPreview" @use="openUse" />
      </div>

      <div class="mp-panel">
        <div class="mp-sec-head">
          <div class="mp-sec-title">行业模板</div>
          <div class="mp-drop">
            <button class="mp-drop-btn" type="button" @click.stop="dropOpen = !dropOpen">
              {{ tradeName }} ▾
            </button>
            <div class="mp-drop-menu" :class="{ 'is-show': dropOpen }">
              <div
                v-for="(row, i) in tradeRows"
                :key="row.key ?? row.name"
                class="mp-drop-item"
                :class="{ 'is-active': tradeIndex === i }"
                @click="pickTrade(i)"
              >
                {{ row.name }}
              </div>
            </div>
          </div>
          <div class="mp-sec-extra" @click="toast('请从行业下拉选择后浏览模板')">查看更多 ›</div>
        </div>
        <MarketTemplateGrid :ids="tradeIds" @preview="openPreview" @use="openUse" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useMarketPortal } from '../useMarketPortal'
import { useMarketPortalStore } from '../useMarketPortalStore'
import MarketFilterBar from '../components/MarketFilterBar.vue'
import MarketPortalSidebar from '../components/MarketPortalSidebar.vue'
import MarketTemplateGrid from '../components/MarketTemplateGrid.vue'
import MarketUviewIcon from '../components/MarketUviewIcon.vue'

defineOptions({ name: 'MarketHomePage' })

const emit = defineEmits<{
  preview: [id: number]
  use: [id: number]
}>()

const { goto, toast } = useMarketPortal()
const { loading, hotIds, festivalTabs, tradeRows, sceneKey, shuffleHot, loadHome } = useMarketPortalStore()

const festIndex = ref(0)
const tradeIndex = ref(0)
const dropOpen = ref(false)

const festIds = computed(() =>
  (festivalTabs.value[festIndex.value]?.templates || []).map((t) => t.id!).filter(Boolean)
)
const tradeIds = computed(() =>
  (tradeRows.value[tradeIndex.value]?.templates || []).map((t) => t.id!).filter(Boolean)
)
const tradeName = computed(() => tradeRows.value[tradeIndex.value]?.name || '行业')

const pickTrade = (i: number) => {
  tradeIndex.value = i
  dropOpen.value = false
}

const onSceneChange = async (key?: number) => {
  sceneKey.value = key
  await loadHome({ sceneKey: key, hotOffset: 0 })
}

const openPreview = (id: number) => emit('preview', id)
const openUse = (id: number) => emit('use', id)

const onDocClick = () => {
  dropOpen.value = false
}

onMounted(() => document.addEventListener('click', onDocClick))
onUnmounted(() => document.removeEventListener('click', onDocClick))
</script>

<style lang="scss" scoped>
.mp-layout {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.mp-main {
  flex: 1;
  min-width: 0;
}

.mp-panel {
  background: #fff;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
}

.mp-sec-head {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  flex-wrap: wrap;
  gap: 6px;
}

.mp-sec-title {
  font-size: 16px;
  font-weight: 700;
}

.mp-shuffle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #2b6de8;
  font-size: 13px;
  cursor: pointer;
  margin-left: 10px;

  &:hover {
    opacity: 0.85;
  }
}

.mp-tabs-row {
  display: flex;
  gap: 12px;
  margin-left: 16px;
  flex-wrap: wrap;
}

.mp-ftab {
  font-size: 14px;
  color: #606266;
  cursor: pointer;
  padding: 2px 4px;

  &.is-active {
    font-weight: 700;
    color: #303133;
    border-bottom: 2px solid #2b6de8;
  }
}

.mp-sec-extra {
  margin-left: auto;
  color: #909399;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    color: #2b6de8;
  }
}

.mp-drop {
  position: relative;
  margin-left: 20px;
}

.mp-drop-btn {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 3px 12px;
  font-size: 13px;
  color: #606266;
  cursor: pointer;
  background: #fff;
}

.mp-drop-menu {
  position: absolute;
  top: 30px;
  left: 0;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  box-shadow: 0 4px 14px rgb(0 0 0 / 10%);
  z-index: 50;
  max-height: 280px;
  overflow-y: auto;
  min-width: 130px;
  display: none;

  &.is-show {
    display: block;
  }
}

.mp-drop-item {
  padding: 7px 14px;
  font-size: 13px;
  color: #606266;
  cursor: pointer;
  white-space: nowrap;

  &:hover,
  &.is-active {
    background: #f0f5ff;
    color: #2b6de8;
  }
}
</style>
